// Copyright (c) 2026 Joshua S. Plaza. All rights reserved.

import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track refresh state to prevent race conditions
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Endpoints where a 401 is an expected business response (bad credentials,
// expired reset link, etc.) — never trigger the refresh/redirect flow.
const AUTH_BYPASS_PATTERNS = [
  '/accounts/login/',
  '/accounts/logout/',
  '/accounts/forgot-password/',
  '/accounts/reset-password/',
];

const shouldBypassAuthFlow = (url = '') =>
  AUTH_BYPASS_PATTERNS.some((pattern) => url.includes(pattern));

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !shouldBypassAuthFlow(originalRequest.url)
    ) {
      originalRequest._retry = true;
      if (isRefreshing) {
        // If refresh is in progress, queue the request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const refreshToken = sessionStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access, refresh } = response.data;
          sessionStorage.setItem('token', access);
          if (refresh) {
            sessionStorage.setItem('refreshToken', refresh);
          }

          processQueue(null, access);

          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } else {
          // No refresh token, logout
          throw new Error('No refresh token');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Refresh failed, logout user
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// API Service functions
export const authAPI = {
  logout: (refreshToken) => api.post('/accounts/logout/', { refresh: refreshToken }),
  updateProfile: (data) => api.put('/accounts/users/update_me/', data),
  updateProfilePicture: (formData) => {
    return api.put('/accounts/users/update_me/', formData);
  },
  removeProfilePicture: () => api.delete('/accounts/users/remove_profile_picture/'),
};

export const usersAPI = {
  getAll: (params) => api.get('/accounts/users/', { params }),
  getById: (id) => api.get(`/accounts/users/${id}/`),
  create: (data) => api.post('/accounts/users/', data),
  update: (id, data) => api.patch(`/accounts/users/${id}/`, data),
  delete: (id) => api.delete(`/accounts/users/${id}/`),
  getTeachers: () => api.get('/accounts/users/teachers/'),
  getStudents: () => api.get('/accounts/users/students/'),
  getStudentDetail: (id) => api.get(`/accounts/users/${id}/student_detail/`),
  getProfile: (id) => api.get(`/accounts/users/${id}/profile/`),
  getStudentStatistics: () => api.get('/accounts/users/student_statistics/'),
  getOnline: () => api.get('/accounts/users/online/'),
  getParents: () => api.get('/accounts/users/parents/'),
  assignParent: (studentId, parentId) => api.post(`/accounts/users/${studentId}/assign_parent/`, { parent_id: parentId }),
  removeParent: (studentId, parentId) => api.post(`/accounts/users/${studentId}/remove_parent/`, { parent_id: parentId }),
  resetPassword: (id, data) => api.post(`/accounts/users/${id}/reset_password/`, data),
};

export const parentProfilesAPI = {
  update: (id, data) => api.patch(`/accounts/parent-profiles/${id}/`, data),
};

export const teacherProfilesAPI = {
  update: (id, data) => api.patch(`/accounts/teacher-profiles/${id}/`, data),
};

export const studentProfilesAPI = {
  update: (id, data) => api.patch(`/accounts/student-profiles/${id}/`, data),
};

export const schoolYearsAPI = {
  getAll: () => api.get('/academics/school-years/'),
  getCurrent: () => api.get('/academics/school-years/current/'),
  create: (data) => api.post('/academics/school-years/', data),
  update: (id, data) => api.patch(`/academics/school-years/${id}/`, data),
  delete: (id) => api.delete(`/academics/school-years/${id}/`),
  setCurrent: (id) => api.post(`/academics/school-years/${id}/set_current/`),
};

export const gradeLevelsAPI = {
  getAll: () => api.get('/academics/grade-levels/'),
  create: (data) => api.post('/academics/grade-levels/', data),
};

export const sectionsAPI = {
  getAll: (params) => api.get('/academics/sections/', { params }),
  getById: (id) => api.get(`/academics/sections/${id}/`),
  create: (data) => api.post('/academics/sections/', data),
  update: (id, data) => api.patch(`/academics/sections/${id}/`, data),
  delete: (id) => api.delete(`/academics/sections/${id}/`),
  getStudents: (id) => api.get(`/academics/sections/${id}/students/`),
};

export const subjectsAPI = {
  getAll: (params) => api.get('/academics/subjects/', { params }),
  create: (data) => api.post('/academics/subjects/', data),
  update: (id, data) => api.patch(`/academics/subjects/${id}/`, data),
  delete: (id) => api.delete(`/academics/subjects/${id}/`),
};

export const classesAPI = {
  getAll: (params) => api.get('/academics/classes/', { params }),
  getById: (id) => api.get(`/academics/classes/${id}/`),
  create: (data) => api.post('/academics/classes/', data),
  update: (id, data) => api.patch(`/academics/classes/${id}/`, data),
  delete: (id) => api.delete(`/academics/classes/${id}/`),
  getMyClasses: () => api.get('/academics/classes/my_classes/'),
  getClassGuardians: (classId) => api.get(`/academics/classes/${classId}/guardians/`),
  getGrades: (id) => api.get(`/academics/classes/${id}/grades/`),
  exportGrades: (id, params) => api.get(`/academics/classes/${id}/export_grades/`, { params, responseType: 'blob' }),
  getAttendance: (id, date) => api.get(`/academics/classes/${id}/attendance/`, { params: { date } }),
};

export const enrollmentsAPI = {
  getAll: (params) => api.get('/academics/enrollments/', { params }),
  getById: (id) => api.get(`/academics/enrollments/${id}/`),
  create: (data) => api.post('/academics/enrollments/', data),
  update: (id, data) => api.patch(`/academics/enrollments/${id}/`, data),
  delete: (id) => api.delete(`/academics/enrollments/${id}/`),
  getMyEnrollment: () => api.get('/academics/enrollments/my_enrollment/'),
  getPublicStatistics: () => api.get('/academics/enrollments/public_statistics/'),
};

export const gradesAPI = {
  bulkInput: (data) => api.post('/academics/grades/bulk_input/', data),
  getMyGrades: () => api.get('/academics/grades/my_grades/'),
  getStudentSummary: (studentId) => api.get('/academics/grades/student_summary/', { params: { student_id: studentId } }),
  getHonorRoll: (params) => api.get('/academics/grades/honor_roll/', { params }),
  getSectionProgress: (params) => api.get('/academics/grades/section_progress/', { params }),
  getSchoolHonorRoll: (params) => api.get('/academics/grades/school_honor_roll/', { params }),
};

export const attendanceAPI = {
  bulkMark: (data) => api.post('/academics/attendance/bulk_mark/', data),
  getMyAttendance: () => api.get('/academics/attendance/my_attendance/'),
  getStudentSummary: (studentId) => api.get('/academics/attendance/student_summary/', { params: { student_id: studentId } }),
  getClassAttendance: (classId, date) => api.get('/academics/attendance/class_attendance/', { params: { class_id: classId, date } }),
};

export const announcementsAPI = {
  getAll: (config = {}) => api.get('/announcements/', config),
  create: (data) => api.post('/announcements/', data),
  update: (id, data) => api.patch(`/announcements/${id}/`, data),
  delete: (id) => api.delete(`/announcements/${id}/`),
  getRecent: () => api.get('/announcements/recent/'),
};

export const backupAPI = {
  download: () => api.get('/accounts/backup/', { responseType: 'blob' }),
  restore: (formData) => api.post('/accounts/backup/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const parentAPI = {
  getChildren: () => api.get('/accounts/my-children/'),
};

export const auditAPI = {
  getAll: (params) => api.get('/accounts/audit-logs/', { params }),
};

export const passwordAPI = {
  forgotPassword: (email) => api.post('/accounts/forgot-password/', { email }),
  resetPassword: (uidb64, token, data) => api.post(`/accounts/reset-password/${uidb64}/${token}/`, data),
};

export const reportsAPI = {
  getPublicReports: () => api.get('/academics/reports/public_reports/'),
  publicDownload: (id) => `${API_BASE_URL}/academics/reports/${id}/public_download/`,
  upload: (formData) => api.post('/academics/reports/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/academics/reports/${id}/`),
  getMyReports: (params) => api.get('/academics/reports/my_reports/', { params }),
  getAllReports: (params) => api.get('/academics/reports/all_reports/', { params }),
  download: (id) => api.get(`/academics/reports/${id}/download/`, { responseType: 'blob' }),
  getReportTypes: () => api.get('/academics/reports/report_types/'),
  getStatistics: () => api.get('/academics/reports/statistics/'),
  getAdminStatistics: () => api.get('/academics/reports/admin_statistics/'),
};
